import { getUrlToLoginPage } from 'pages/urls';
import React, {useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { TopNav } from 'shared/components/TopNav/index.js';
import { executeRecaptcha } from "api/recaptcha";
import { TextInput } from "components/TextInput";
import { ActionButton } from "components/ActionButton";
import { HttpApiResponseError } from "shared/http-api";
import antidoteLogoSrc from "images/antidote-logo.png";
import { patchUserPassword, sendPasswordResetCode } from 'api';
import { CheckCircleOutline } from '@material-ui/icons';
import { ExpiryCountdown } from 'components/ExpiryCountdown';
import { PasswordInput } from 'components/PasswordInput';

const UI_STATES = {
  ENTER_ACCOUNT_DETAILS: '1',
  ENTER_CODE: '2',
  ENTER_PASSWORD: '3',
  RESET_SUCCESS: '4'
}

type ResetCodeInfoT = { identifier: string, expiresAt: string, isEmail: boolean };

const LOGIN_PAGE_URL = getUrlToLoginPage();

export function ForgotPasswordPage () {
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const code = urlSearchParams.get('code');
  const identifier = urlSearchParams.get('identifier');
  const expiresAt = urlSearchParams.get('expiresAt');
  const redirectTo = urlSearchParams.get('redirectTo') || '';

  const loginPageUrl = LOGIN_PAGE_URL + '?' + `redirectTo=${redirectTo}`;

  const [uiState, setUiState] = useState(
    code && identifier && expiresAt
      ? UI_STATES.ENTER_PASSWORD
      : UI_STATES.ENTER_ACCOUNT_DETAILS
  );

  const formElem = (() => {
    switch (uiState) {
      case UI_STATES.ENTER_CODE:
        if (!expiresAt || !identifier) return setUiState(UI_STATES.ENTER_ACCOUNT_DETAILS);
        return (
          <CodeForm
            resetCodeInfo={{ expiresAt, identifier, isEmail: identifier.includes('@') }}
            onContinue={(code: string) => {
              setUiState(UI_STATES.ENTER_PASSWORD);
              setUrlSearchParams({ redirectTo, code, identifier, expiresAt });
            }}
          />
        );
      case UI_STATES.ENTER_PASSWORD:
        if (!code || !identifier) return setUiState(UI_STATES.ENTER_ACCOUNT_DETAILS);
        return (
          <ResetPasswordForm
            code={code}
            identifier={identifier}
            onReset={() => {
              setUiState(UI_STATES.RESET_SUCCESS);
              setTimeout(() => navigate(loginPageUrl), 2000);
            }}
          />
        );
      case UI_STATES.RESET_SUCCESS:
        return (
          <div className="pt-4 text-center text-lg">
            <CheckCircleOutline color="primary" style={{ width: '4.8rem', height: '4.8rem' }} />
            <div className='mt-4'>Your password has been reset.</div>
            <div> You will be redirected shortly. </div>
          </div>
        );
      default:
      case UI_STATES.ENTER_ACCOUNT_DETAILS:
        return (
          <AccountDetailsForm
            onCodeSent={({ identifier, expiresAt }) => {
              setUrlSearchParams({ redirectTo, identifier, expiresAt });
              setUiState(UI_STATES.ENTER_CODE);
            }}
          />
        );
    }
  })() || null;

  const gotoLoginPage = () => {
    navigate(loginPageUrl);
  };

  return (
    <div className="px-6 py-4">
      <TopNav onBackClick={gotoLoginPage} />
      <div className="max-w-[350] mx-auto flex flex-col justify-center mt-12 gap-2">
        <img src={antidoteLogoSrc} alt='Antidote Logo' className="w-full" />
        <div className="text-lg mb-4 text-gray-900 font-medium text-center">
          Reset Your Password
        </div>
        {formElem}
        {expiresAt && uiState !== UI_STATES.RESET_SUCCESS ? (
          <div className='text-violet-600 font-bold text-sm my-2 text-left'>
            <ExpiryCountdown subject='Code' expiresAt={expiresAt} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

function AccountDetailsForm ({ onCodeSent }: { onCodeSent: (data: ResetCodeInfoT) => any }) {
  const [identifier, setIdentifier] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<null|string>(null);

  const canSubmit = parseInt(identifier).toString().length === 10 || identifier.match(/^\S+@\S+\.\S+$/);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha('send_password_reset_code');
      const { expiresAt, isEmail } = await sendPasswordResetCode({ identifier, token: recaptchaToken });
      onCodeSent({ identifier, expiresAt, isEmail });
    } catch (err) {
      if (!(err instanceof HttpApiResponseError)) {
        setError('Could not send OTP because of an unknown error');
      } else {
        const response = await err.json();
        const identifierError = response.errors && response.errors.find(e => e.path === 'identifier');
        const tokenError = response.errors && response.errors.find(e => e.path === 'token');
        if (identifierError) {
          setError(identifierError.message);
        } else if (tokenError) {
          setError(tokenError.message);
        } else if (err.isTooManyRequests) {
          setError(response.message);
        } else {
          setError('Could not send OTP because of an unknown error');
        }
      }
    }
    setSubmitting(false);
  }

  return (
    <form noValidate onSubmit={onSubmit}>
      <p className="text-gray-500 font-medium mb-4">
        Enter the email associated with your Antidote account. 
      </p>
      <p className="text-gray-500 font-light text-sm my-4">
        A Password reset code will be sent to this email if it exists in our system.
      </p>
      <TextInput
        name='identifier'
        value={identifier}
        placeholder="Enter your email"
        onChange={(value) => {
          setError(null);
          setIdentifier(value);
        }}
        required
        error={error || undefined}
      />
      <div className="mt-2">
        <ActionButton
          type="submit"
          label="Send Code"
          workingLabel="Sending Confirmation Code"
          working={submitting}
          disabled={!canSubmit}
        />
      </div>
    </form>
  )
}

function CodeForm ({ resetCodeInfo, onContinue }: { resetCodeInfo: ResetCodeInfoT, onContinue: (code: string) => any }) {
  const [code, setCode] = useState('');

  const canSubmit = !!code;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    onContinue(code);
  }

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className="text-gray-500 font-medium mb-4">
        Enter the code that was sent to your {resetCodeInfo.isEmail ? 'email' : 'phone number'}
      </div>
      <TextInput
        name='code'
        value={code}
        placeholder="Enter code"
        onChange={setCode}
        required
      />
      <div className="mt-2">
        <ActionButton
          type="submit"
          label="Continue"
          disabled={!canSubmit}
        />
      </div>
    </form>
  )
}

function ResetPasswordForm ({ code: codeProp, identifier, onReset }: { code: string, identifier: string, onReset: () => any }) {
  const [code, setCode] = useState(codeProp);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string,string>>({});
  const [showCodeInput, setShowCodeInput] = useState(false);

  const canSubmit = password && confirmPassword;

  const checkErrors = () => {
    const errors: Record<string,string> = {};
    if (!password) errors.password = 'Password is required';
    else if (password.length < 6) errors.password = 'Password must be atleast 6 characters';
    if (!confirmPassword) errors.confirmPassword = 'Password confirmation is required';
    if (password !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    setErrors(errors);
    return Object.keys(errors).length > 0;
  }

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (checkErrors()) return;
    setSubmitting(true);
    try {
      await patchUserPassword({ identifier, password, code });
      onReset();
    } catch (err) {
      if (!(err instanceof HttpApiResponseError)) {
        setErrors({ password: 'Could not reset your password because of an unknown error' });
      } else {
        setShowCodeInput(true);
        const response = await err.json();
        if (!response.errors || response.errors.length === 0) {
          setErrors({ name: 'Could not reset your password because of an unknown error' });
        } else {
          setErrors(Object.fromEntries(response.errors.map(err => [err.path, err.message])));
        }
      }
    }
    setSubmitting(false);
  }

  return (
    <form noValidate onSubmit={onSubmit}>
      {showCodeInput ? (
        <TextInput
          label='Code'
          name='code'
          value={code}
          placeholder="Enter code"
          onChange={setCode}
          required
          error={errors.code}
          autoComplete='off'
        />
      ): null}
      <PasswordInput
        label='New Password'
        name='password'
        value={password}
        placeholder="Enter new password"
        onChange={(value) => {
          setErrors({});
          setPassword(value);
        }}
        required
        error={errors.password}
      />
      <PasswordInput
        label='Confirm New Password'
        name='confirmPassword'
        value={confirmPassword}
        placeholder="Enter new password again"
        onChange={(value) => {
          setConfirmPassword(value);
          const length = value.length;
          const check = password.substring(0, length);
          if(password !== check) {
            setErrors({ confirmPassword: "Passwords do not match" });
          } else {
            setErrors({});
          }
        }}
        required
        error={errors.confirmPassword}
      />
      <div className="mt-2">
        <ActionButton
          type="submit"
          label="Change Password"
          workingLabel="Resetting Your Password"
          working={submitting}
          disabled={!canSubmit}
        />
      </div>
    </form>
  )
}
